import {
  Box,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  Button,
  Grid,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { ConstantActions } from '../../../../Services/_actions/index.js';
import { dateFormate, generateFilterKey } from '../../../../utils/helper.jsx';
import { useNavigate } from 'react-router';
import { calculatePercentageOfTotal, CategoryOptions } from '../../Template/template.constant.js';
import Pagination from '../../../FlowBuilder/Common/Pagination/index.tsx';
import SearchBox from '../../../FlowBuilder/Common/SearchBox/index.tsx';
import ScheduleTemplate from './ScheduleTemplate.tsx';
// import ScheduleTemplate from '../../Template/ScheduleTemplate.jsx';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  channelListAtom,
  templateTableStateAtom,
  userInformationAtom,
  WhatsAppTempFormAtom,
} from '../../../../Services/_states/index.js';
import {
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import { useTheme } from '@mui/material/styles';
import SearchableDropdown from '../../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import FilterDrawer from '../../../Shared/FilterDrawer/index.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendTemplateModal from './SendTemplateModal.tsx';
import OrganisationDropdown from '../../../Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../../Shared/BotNumberDropdown/index.tsx';
import HelpSidebar from './HelpSidebar.tsx';
import HomeIcon from '@mui/icons-material/Home';
import useDebounce from '../../../Shared/Delay/index.tsx';
import DynamicBreadcrumbs from '../../../Shared/Breadcrumb/index.tsx';
import { fetchTemplateListSelector } from '../../../../Services/_states/Template/templateSelector.ts';
import CommonTable, { EllipsisWithTooltip } from '../../../Shared/Tables/TableV1.tsx';
import CommonBadges from '../../../Shared/Badges/index.jsx';
import { globalpermissions } from '../../../../Services/Permission.js';

type ColumnObj = {
  index: number;
  name: string;
  category: string;
  status: string;
  language: string;
  toggle: string;
  updated_at: string;
  edit?: string;
  description?: string;
};

const TemplateListV1 = () => {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const resetTempFormData = useResetRecoilState(WhatsAppTempFormAtom);
  const setWhatsAppTempForm = useSetRecoilState(WhatsAppTempFormAtom);
  const navigate = useNavigate();
  const [showSendTemplate, setShowSendTemplate] = useState(false);
  const [tableState, setTableState] = useRecoilState(templateTableStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchTemplateListSelector);
  const constActions = ConstantActions();

  const resetTableState = () => {
    setTableState((prev) => ({
      ...prev,
      // cache: {}, // Clear cached data to ensure fresh fetch
      // totalCountCache: {},
      filters: {
        limit: 10,
        search: '',
        page: 0,
        sortOrder: 'desc',
        sortField: 'created_at',
        category: '',
        botNumber: '',
        organisation: '',
        keyStakeholder: '',
        status: '',
        startDate: null,
        endDate: null,
        isActive: '',
        wa_phone_number: '',
        channelObj: null,
        categoryObj: null,
        organisationObj: null,
        statusObj: null,
      },
    }));
    // setTimeout(() => {
    //     setTableState((prev) => ({ ...prev }));
    // }, 10);
  };
  const [searchInput, setSearchInput] = useState(tableState.filters.search || '');

  const debouncedSearch = useDebounce(searchInput, 500);

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  const handleChangePage = (event, newPage) => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: newPage },
    }));
  };
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10) || 10;
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, limit: value },
    }));
  };

  const handleSearch = (query: string) => {
    setTableState((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        search: query,
        page: 0,
      },
    }));
  };

  const templateDetailsPage = (data) => {
    navigate(`/r/whatsapp/template-details/${data?.name}/${data?.bot_number}`, {});
  };
  const createTemplate = () => {
    resetTempFormData();
    navigate('/r/whatsapp/create-template');
  };

  useEffect(() => {
    if (sorting?.length > 0) {
      const { id, desc } = sorting[0];
      const updatedFilters = {
        ...tableState?.filters,
        sortField: id || 'created_at',
        sortOrder: desc ? 'desc' : 'asc',
      };
      setTableState((prev) => ({ ...prev, filters: { ...updatedFilters } }));
    }
  }, [sorting]);

  // useEffect(() => {
  //   setTableState((prev) => ({
  //     ...prev,
  //     filters: {
  //       ...prev.filters,
  //       search: debouncedSearch,
  //       page: 0,
  //     },
  //   }));
  // }, [debouncedSearch, setTableState]);
  useEffect(() => {
    if (debouncedSearch !== tableState.filters.search) {
      setTableState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          search: debouncedSearch,
          page: 0,
        },
      }));
    }
  }, [debouncedSearch]);

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Templates' },
  ];

  const columns = useMemo<MRT_ColumnDef<ColumnObj>[]>(
    () => [
      {
        accessorKey: 'index',
        header: '#',
        enableSorting: false,
        size: 50,
        Cell: ({ row, table }) => {
          const rowIndex = row.index;
          const page = tableState?.filters?.page;
          return page * tableState?.filters?.limit + rowIndex + 1;
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          return (
            <Typography
              variant="body2"
              color="primary.main"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                templateDetailsPage(row?.original);
              }}
            >
              <EllipsisWithTooltip text={value ?? ''} />
            </Typography>
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell?.getValue()?.toString();
          return <EllipsisWithTooltip text={value ?? ''} />
        },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        enableSorting: false,
        size: 120,
        Cell: ({ cell, row }) => {
          const value = cell?.getValue()?.toString();
          if (value) {
            return (
              <CommonBadges value={value ?? ''} color={value?.toUpperCase() === "UTILITY" ? "success" : value?.toUpperCase() === "MARKETING" ? "info" : ""} />
            )
          } else return null
        },

      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        size: 120,
        Cell: ({ cell, row }) => {
          const value = cell?.getValue()?.toString();
          return (
            <CommonBadges value={value ?? ''} color={value?.toUpperCase() === "APPROVED" ? "success" : value?.toUpperCase() === "PENDING" ? "warning" : "error"} />
          )
        },
      },
      {
        accessorKey: 'language',
        header: 'Language',
        enableSorting: false,
        size: 100,
      },
      // {
      //     accessorKey: 'sent_count',
      //     header: (
      //         <Tooltip title="Percentage of messages sent successfully out of total users">
      //             <span>S (%)</span>
      //         </Tooltip>
      //     ),
      //     size: 80,
      //     Cell: ({ row }: any) => calculatePercentageOfTotal(row?.original?.sent_count, row?.original?.total_users),
      // },
      // {
      //     accessorKey: 'delivered_count',
      //     header: (
      //         <Tooltip title="Percentage of messages delivered successfully out of total users">
      //             <span>D (%)</span>
      //         </Tooltip>
      //     ),
      //     size: 80,
      //     Cell: ({ row }: any) => calculatePercentageOfTotal(row?.original?.delivered_count, row?.original?.total_users),
      // },
      // {
      //     accessorKey: 'read_count',
      //     header: (
      //         <Tooltip title="Percentage of messages read out of total users" >
      //             <span>R (%)</span>
      //         </Tooltip>
      //     ),
      //     size: 80,
      //     Cell: ({ row }: any) => calculatePercentageOfTotal(row?.original?.read_count, row?.original?.total_users),
      // },
      // {
      //     accessorKey: 'toggle',
      //     header: 'Active/Inactive',
      //     enableSorting: false,
      //     size: 120,
      //     Cell: ({ row }) => {
      //         const [isActive, setIsActive] = useState(row.original.toggle === 'Active');

      //         const handleToggle = () => {
      //             setIsActive(!isActive);
      //             console.log(`${row.original.name} is now ${!isActive ? 'Active' : 'Inactive'}`);
      //         };

      //         return (
      //             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      //                 <Tooltip title={isActive ? 'Deactivate' : 'Activate'} arrow>
      //                     <Switch
      //                         checked={isActive}
      //                         onChange={handleToggle}
      //                         color="primary"
      //                     />
      //                 </Tooltip>
      //                 <span style={{ marginLeft: 8 }}>{isActive ? 'Active' : 'Inactive'}</span>
      //             </div>
      //         );
      //     },
      // },
      {
        accessorKey: 'updated_at',
        header: 'Last Updated',
        size: 150,
        Cell: ({ row }) => dateFormate(new Date(row.original.updated_at), 'DD-MM-YYYY hh:mm A'),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        enableSorting: false,
        size: 60,
        Cell: ({ row }: any) => {
          const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);

          const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <>
              {/* <IconButton onClick={handleClick}> */}
              <MoreVertIcon fontSize="small" onClick={handleClick} cursor="pointer" />
              {/* </IconButton> */}
              <Menu
                id={`menu-${row.index}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': `menu-button-${row.index}`,
                }}
              >
                {constActions.userHasPermission([
                  globalpermissions?.templateDashboard?.editTemplates,
                ]) && (
                    <MenuItem
                      onClick={() =>
                        navigate(
                          `/r/whatsapp/edit-template/${row?.original?.name}/${row?.original?.bot_number}`
                        )
                      }
                    >
                      Edit
                    </MenuItem>
                  )}
                {row?.original?.status?.toLowerCase() === 'approved' && (
                  <MenuItem
                    onClick={() => {
                      setWhatsAppTempForm(row?.original);
                      setShowSendTemplate(true);
                      handleClose();
                    }}
                  >
                    Send
                  </MenuItem>
                )}
              </Menu>
            </>
          );
        },
      },
    ],
    [tableState?.filters]
  );
  return (
    <Box
      sx={{
        // backgroundColor: '#f8f9fa',
        // borderRadius: '8px',
        // boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        // padding: '5px',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 'bold', fontSize: { xs: '20px', sm: '2.125rem;' } }}
          >
            Templates
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          {constActions.userHasPermission([globalpermissions?.templateDashboard?.addTemplates]) && (

            <Button variant="contained" color="primary" size="small" onClick={createTemplate}>
              Add
            </Button>
          )}
          <Tooltip title="Help">
            <IconButton>
              <HelpSidebar />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          alignItems: 'center',
          paddingTop: '8px',
          paddingBottom: { xs: '10px', sm: '10px', md: '20px' },
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'end',
            flexWrap: 'wrap',
            gap: { xs: '10px', md: 2, lg: 2 },
            justifyContent: 'space-between',
            margin: { sm: '0px', md: '0px', lg: '0px' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              width: { xs: '100%', sm: 'auto', md: 'auto' },
            }}
          >
            <SearchBox handleSearch={(value) => setSearchInput(value)} value={searchInput} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'left', sm: 'center', md: 'center' },
              gap: { xs: '10px', sm: '5px', md: '10px', lg: '10px' },
              m: { xs: '0px', md: 'unset', lg: 'unset' },
              flexWrap: { xs: 'wrap', sm: 'nowrap', md: 'nowrap' },
            }}
          >
            {loggedInUser?.is_superuser && (
              <OrganisationDropdown
                sx={{ minWidth: 165 }}
                value={tableState?.filters?.organisationObj || null}
                onChange={(selected) => {
                  const newFilters = {
                    ...tableState?.filters,
                    organisation: selected?.organisation_id || '',
                    organisationObj: selected,
                    channelObj: null,
                    botNumber: '',
                    page: 0,
                  };
                  setTableState((prev) => ({
                    ...prev,
                    filters: { ...newFilters },
                  }));
                }}
                label={'Select Organisation'}
              />
            )}

            <BotNumberDropdown
              sx={{ minWidth: 160 }}
              organisation={
                loggedInUser?.is_superuser
                  ? tableState?.filters?.organisationObj
                  : loggedInUser?.profile?.organisation || null
              }
              onChange={(selected) => {
                const newFilters = {
                  ...tableState?.filters,
                  botNumber: selected?.wa_phone_number || '',
                  channelObj: selected,
                  page: 0,
                };
                setTableState((prev) => ({
                  ...prev,
                  filters: { ...newFilters },
                }));
              }}
              value={tableState?.filters?.channelObj}
              label="Select Bot Number"
            />

            <Box
              sx={{
                display: 'flex',
                gap: { xs: '10px', sm: '5px', md: '10px', lg: '10px' },
                flexWrap: { xs: 'wrap', sm: 'nowrap', md: 'nowrap' },
                width: { xs: '100%', sm: 'auto', md: 'auto' },
              }}
            >
              <SearchableDropdown
                sx={{ minWidth: 100 }}
                options={[
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'PENDING	', label: 'Pending' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={tableState?.filters?.statusObj}
                onChange={(selected) => {
                  const newFilters = {
                    ...tableState?.filters,
                    status: selected?.value || '',
                    statusObj: selected,
                    page: 0,
                  };
                  setTableState((prev) => ({
                    ...prev,
                    filters: { ...newFilters },
                  }));
                }}
                label="Status"
                multiSelect={false}
                getOptionLabel={(option) => option?.label}
              />
              <SearchableDropdown
                sx={{ minWidth: 120 }}
                options={CategoryOptions.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                value={tableState?.filters?.categoryObj}
                onChange={(selected) => {
                  const newFilters = {
                    ...tableState?.filters,
                    category: selected?.value || '',
                    categoryObj: selected,
                    page: 0,
                  };
                  setTableState((prev) => ({
                    ...prev,
                    filters: { ...newFilters },
                  }));
                }}
                label="Category"
                multiSelect={false}
                getOptionLabel={(option) => option.label}
              />
            </Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => resetTableState()}
              sx={{
                p: { xs: '8px 30px', sm: '5px', md: '5px', lg: '5px 15px' },
                mx: { xs: 'auto', md: 'auto' },
              }}
            >
              Clear
            </Button>
          </Box>
        </Grid>
      </Grid>
      <CommonTable
        columns={columns}
        data={tableState?.cache[generateFilterKey(tableState?.filters)] || []}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 310px)',
            // width: '100%',
            // overflowY: 'auto',
            // '&::-webkit-scrollbar': {
            //   width: '6px',
            // },
          },
        }}
        enableStickyHeader={true}
        manualSorting={true}
        state={{ isLoading: fetchedData.state === 'loading', sorting: sorting }}
        onSortingChange={setSorting}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
              rowsPerPage={tableState?.filters?.limit}
              page={tableState?.filters?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <ScheduleTemplate show={showScheduleModal} onHide={() => setShowScheduleModal(false)} />
      {showSendTemplate && (
        <SendTemplateModal open={showSendTemplate} onClose={() => setShowSendTemplate(false)} />
      )}
    </Box>
  );
};

export default TemplateListV1;
